/* eslint-disable */
import {
    ReactNode,
    useCallback,
    useLayoutEffect,
    useRef,
    useState
} from "react";
import {
    createPath,
    NavigationType,
    parsePath,
    Router,
    To
} from "react-router-dom";

/** This is slightly modified code taken from react-router-dom's HashRouter component.
 *  https://github.com/remix-run/react-router/blob/main/packages/react-router/lib/dom/lib.tsx
 *  HashRouter has been modified not to use startTransition.
 *  This fixes an issue where if you use the browser's forward or back button, a transition would start but there is no visual indication of the transition.
 *  When this happens and there are any slow API calls, it is not obvious that the browser has detected your navigation.
 *  (All links and buttons within the app use startTransition so this is not an issue for those.)
 */
export function CustomHashRouter({
    basename,
    children,
    window
}: {
    basename?: string;
    children: ReactNode;
    window?: unknown;
}) {
    "use no memo";

    let historyRef = useRef<any>();
    if (historyRef.current == null) {
        historyRef.current = createHashHistory({ window, v5Compat: true });
    }

    let history = historyRef.current;
    let [state, setStateImpl] = useState({
        action: history.action,
        location: history.location
    });
    let setState = useCallback(
        (newState: { action: NavigationType; location: Location }) => {
            // startTransition(() => setStateImpl(newState)); // <-- jezz 2025-04-03 intentionally not using startTransition here
            setStateImpl(newState);
        },
        [setStateImpl]
    );

    useLayoutEffect(() => history.listen(setState), [history, setState]);

    return (
        <Router
            basename={basename}
            children={children}
            location={state.location}
            navigationType={state.action}
            navigator={history}
        />
    );
}

enum Action {
    Pop = "POP",
    Push = "PUSH",
    Replace = "REPLACE"
}

const PopStateEventType = "popstate";

function createHashHistory(options = {}) {
    function createHashLocation(
        window: Window,
        globalHistory: Window["history"]
    ) {
        let {
            pathname = "/",
            search = "",
            hash = ""
        } = parsePath(window.location.hash.substring(1));

        // Hash URL should always have a leading / just like window.location.pathname
        // does, so if an app ends up at a route like /#something then we add a
        // leading slash so all of our path-matching behaves the same as if it would
        // in a browser router.  This is particularly important when there exists a
        // root splat route (<Route path="*">) since that matches internally against
        // "/*" and we'd expect /#something to 404 in a hash router app.
        if (!pathname.startsWith("/") && !pathname.startsWith(".")) {
            pathname = "/" + pathname;
        }

        return createLocation(
            "",
            { pathname, search, hash },
            // state defaults to `null` because `window.history.state` does
            (globalHistory.state && globalHistory.state.usr) || null,
            (globalHistory.state && globalHistory.state.key) || "default"
        );
    }

    function createHashHref(window: Window, to: To) {
        let base = window.document.querySelector("base");
        let href = "";

        if (base && base.getAttribute("href")) {
            let url = window.location.href;
            let hashIndex = url.indexOf("#");
            href = hashIndex === -1 ? url : url.slice(0, hashIndex);
        }

        return href + "#" + (typeof to === "string" ? to : createPath(to));
    }

    function validateHashLocation(location: Location, to: To) {
        warning(
            location.pathname.charAt(0) === "/",
            `relative pathnames are not supported in hash history.push(${JSON.stringify(
                to
            )})`
        );
    }

    return getUrlBasedHistory(
        createHashLocation,
        createHashHref,
        validateHashLocation,
        options
    );
}

function invariant(value: any, message?: string) {
    if (value === false || value === null || typeof value === "undefined") {
        throw new Error(message);
    }
}

function warning(cond: any, message: string) {
    if (!cond) {
        if (typeof console !== "undefined") console.warn(message);

        try {
            // Welcome to debugging history!
            //
            // This error is thrown as a convenience, so you can more easily
            // find the source for a warning that appears in the console by
            // enabling "pause on exceptions" in your JavaScript debugger.
            throw new Error(message);
        } catch (e) {}
    }
}

function createLocation(
    current: string | any,
    to: To,
    state: any = null,
    key?: string
): Readonly<Location> {
    let location: Readonly<any> = {
        pathname: typeof current === "string" ? current : current.pathname,
        search: "",
        hash: "",
        ...(typeof to === "string" ? parsePath(to) : to),
        state,
        // TODO: This could be cleaned up.  push/replace should probably just take
        // full Locations now and avoid the need to run through this flow at all
        // But that's a pretty big refactor to the current test suite so going to
        // keep as is for the time being and just let any incoming keys take precedence
        key: (to && (to as any).key) || key || createKey()
    };
    return location;
}

function createKey() {
    return Math.random().toString(36).substring(2, 10);
}

function getHistoryState(location: any, index: number) {
    return {
        usr: location.state,
        key: location.key,
        idx: index
    };
}

function getUrlBasedHistory(
    getLocation: (window: Window, globalHistory: Window["history"]) => Location,
    createHref: (window: Window, to: To) => string,
    validateLocation: ((location: Location, to: To) => void) | null,
    options: any = {}
) {
    let { window = document.defaultView!, v5Compat = false } = options;
    let globalHistory = window.history;
    let action = Action.Pop;
    let listener: any | null = null;

    let index = getIndex()!;
    // Index should only be null when we initialize. If not, it's because the
    // user called history.pushState or history.replaceState directly, in which
    // case we should log a warning as it will result in bugs.
    if (index == null) {
        index = 0;
        globalHistory.replaceState({ ...globalHistory.state, idx: index }, "");
    }

    function getIndex(): number {
        let state = globalHistory.state || { idx: null };
        return state.idx;
    }

    function handlePop() {
        action = Action.Pop;
        let nextIndex = getIndex();
        let delta = nextIndex == null ? null : nextIndex - index;
        index = nextIndex;
        if (listener) {
            listener({ action, location: history.location, delta });
        }
    }

    function push(to: To, state?: any) {
        action = Action.Push;
        let location = createLocation(history.location, to, state);
        if (validateLocation) validateLocation(location, to);

        index = getIndex() + 1;
        let historyState = getHistoryState(location, index);
        let url = history.createHref(location);

        // try...catch because iOS limits us to 100 pushState calls :/
        try {
            globalHistory.pushState(historyState, "", url);
        } catch (error) {
            // If the exception is because `state` can't be serialized, let that throw
            // outwards just like a replace call would so the dev knows the cause
            // https://html.spec.whatwg.org/multipage/nav-history-apis.html#shared-history-push/replace-state-steps
            // https://html.spec.whatwg.org/multipage/structured-data.html#structuredserializeinternal
            if (
                error instanceof DOMException &&
                error.name === "DataCloneError"
            ) {
                throw error;
            }
            // They are going to lose state here, but there is no real
            // way to warn them about it since the page will refresh...
            window.location.assign(url);
        }

        if (v5Compat && listener) {
            listener({ action, location: history.location, delta: 1 });
        }
    }

    function replace(to: To, state?: any) {
        action = Action.Replace;
        let location = createLocation(history.location, to, state);
        if (validateLocation) validateLocation(location, to);

        index = getIndex();
        let historyState = getHistoryState(location, index);
        let url = history.createHref(location);
        globalHistory.replaceState(historyState, "", url);

        if (v5Compat && listener) {
            listener({ action, location: history.location, delta: 0 });
        }
    }

    function createURL(to: To): URL {
        // window.location.origin is "null" (the literal string value) in Firefox
        // under certain conditions, notably when serving from a local HTML file
        // See https://bugzilla.mozilla.org/show_bug.cgi?id=878297
        let base =
            window.location.origin !== "null"
                ? window.location.origin
                : window.location.href;

        let href = typeof to === "string" ? to : createPath(to);
        // Treating this as a full URL will strip any trailing spaces so we need to
        // pre-encode them since they might be part of a matching splat param from
        // an ancestor route
        href = href.replace(/ $/, "%20");
        invariant(
            base,
            `No window.location.(origin|href) available to create URL for href: ${href}`
        );
        return new URL(href, base);
    }

    let history = {
        get action() {
            return action;
        },
        get location() {
            return getLocation(window, globalHistory);
        },
        listen(fn: any) {
            if (listener) {
                throw new Error("A history only accepts one active listener");
            }
            window.addEventListener(PopStateEventType, handlePop);
            listener = fn;

            return () => {
                window.removeEventListener(PopStateEventType, handlePop);
                listener = null;
            };
        },
        createHref(to: any) {
            return createHref(window, to);
        },
        createURL,
        encodeLocation(to: any) {
            // Encode a Location the same way window.location would
            let url = createURL(to);
            return {
                pathname: url.pathname,
                search: url.search,
                hash: url.hash
            };
        },
        push,
        replace,
        go(n: any) {
            return globalHistory.go(n);
        }
    };

    return history;
}
